/**
 * 渠道客户用量 · 库存日历「渠道视图」共享组件（2026-07-15 老板需求·示意图已审）
 *
 * - useChannelView: 开关状态（localStorage 持久·默认关）
 * - ChannelToggle: 开关按钮 + 三色图例
 * - ChannelStackBar: 格子底部三色堆叠条（同行蓝 / 平台紫 / 散客绿）
 * - UsageDrawer: 点格子右侧抽屉·按渠道分组列客户名+用量+单数·展开跳订单
 *
 * 数据契约：services/usage.ts → UsageMonthlyRead（渠道归组在后端）
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import type { ChannelGroup, UsageCustomerEntry } from "@/services/types/usage";

export const CHANNEL_META: Record<
  ChannelGroup,
  { label: string; bar: string; text: string }
> = {
  b2b: { label: "同行 B2B", bar: "bg-blue-500", text: "text-blue-700" },
  ota: { label: "平台 OTA", bar: "bg-violet-500", text: "text-violet-700" },
  direct: { label: "散客直客", bar: "bg-emerald-500", text: "text-emerald-700" },
};
const GROUPS: ChannelGroup[] = ["b2b", "ota", "direct"];

const LS_KEY = "ota_channel_view";

/** 渠道视图开关（localStorage 持久·默认关） */
export function useChannelView(): [boolean, () => void] {
  const [on, setOn] = useState(() => localStorage.getItem(LS_KEY) === "1");
  const toggle = () => {
    setOn((v) => {
      localStorage.setItem(LS_KEY, v ? "0" : "1");
      return !v;
    });
  };
  return [on, toggle];
}

/** 按子实体过滤后求三渠道用量（subId 缺省 = 全天所有子实体） */
export function channelSums(
  entries: UsageCustomerEntry[] | undefined,
  subId?: string,
): Record<ChannelGroup, number> & { used: number } {
  const sums = { b2b: 0, ota: 0, direct: 0, used: 0 };
  for (const e of entries ?? []) {
    if (subId !== undefined && e.sub_resource_id !== subId) continue;
    sums[e.channel_group] += e.qty;
    sums.used += e.qty;
  }
  return sums;
}

export function ChannelToggle({ on, onToggle }: { on: boolean; onToggle: () => void }) {
  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      {on && (
        <span className="flex items-center gap-2 text-[11px]">
          {GROUPS.map((g) => (
            <span key={g} className={`inline-flex items-center gap-1 ${CHANNEL_META[g].text}`}>
              <span className={`inline-block h-2.5 w-2.5 rounded-sm ${CHANNEL_META[g].bar}`} />
              {CHANNEL_META[g].label}
            </span>
          ))}
        </span>
      )}
      <button
        onClick={onToggle}
        title="叠加显示各渠道（同行/平台/散客）用量"
        className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-600 hover:text-slate-800"
      >
        {!on && (
          <span className="flex gap-0.5">
            {GROUPS.map((g) => (
              <span key={g} className={`inline-block h-2 w-2 rounded-[2px] ${CHANNEL_META[g].bar}`} />
            ))}
          </span>
        )}
        渠道视图
        <span
          className={`relative inline-flex h-[18px] w-[34px] shrink-0 items-center rounded-full p-0.5 transition-colors ${on ? "bg-blue-600" : "bg-slate-300"}`}
        >
          <span
            className={`h-[14px] w-[14px] rounded-full bg-white shadow transition-transform ${on ? "translate-x-4" : "translate-x-0"}`}
          />
        </span>
      </button>
    </span>
  );
}

/**
 * 三色堆叠条。denominator = total（有总量时·余下部分留底色）或 used（无总量时铺满）。
 */
export function ChannelStackBar({
  sums,
  total,
  className = "",
}: {
  sums: Record<ChannelGroup, number> & { used: number };
  total?: number | null;
  className?: string;
}) {
  const denom = total && total > 0 ? total : sums.used;
  if (denom <= 0) return null;
  return (
    <div className={`flex h-1.5 w-full overflow-hidden rounded-full bg-black/10 ${className}`}>
      {GROUPS.map((g) =>
        sums[g] > 0 ? (
          <div
            key={g}
            className={`h-full ${CHANNEL_META[g].bar}`}
            style={{ width: `${Math.min(100, (sums[g] / denom) * 100)}%` }}
          />
        ) : null,
      )}
    </div>
  );
}

/** 按渠道分组的客户明细列表（Drawer 内部 / 页面内嵌卡片两用） */
export function UsageBreakdownList({
  entries,
  unit,
  loading,
}: {
  entries: UsageCustomerEntry[];
  unit: string;
  loading?: boolean;
}) {
  const [expanded, setExpanded] = useState<string | null>(null);
  const sums = channelSums(entries);
  if (sums.used === 0) {
    return (
      <p className="rounded-lg bg-slate-50 py-4 text-center text-sm text-slate-400">
        {loading ? "加载中…" : "当日无已确认订单占用"}
      </p>
    );
  }
  return (
    <>
      {GROUPS.map((g) => {
        const groupEntries = entries
          .filter((e) => e.channel_group === g)
          .sort((a, b) => b.qty - a.qty);
        if (groupEntries.length === 0) return null;
        return (
          <div key={g} className="mb-3 last:mb-0">
            <p className={`mb-1.5 flex items-center gap-1.5 text-xs font-semibold ${CHANNEL_META[g].text}`}>
              <span className={`inline-block h-2.5 w-2.5 rounded-sm ${CHANNEL_META[g].bar}`} />
              {CHANNEL_META[g].label} · {sums[g]} {unit}
            </p>
            <div className="divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white">
              {groupEntries.map((e, i) => {
                const rowKey = `${g}-${e.customer_id ?? e.customer_name}-${e.sub_resource_id ?? ""}-${i}`;
                return (
                  <div key={rowKey}>
                    <button
                      onClick={() => setExpanded((v) => (v === rowKey ? null : rowKey))}
                      className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-slate-50"
                    >
                      <span className="font-medium text-slate-800">
                        {e.customer_name}
                        {e.source && g === "direct" && (
                          <span className="ml-1.5 text-[10px] font-normal text-slate-400">{e.source}</span>
                        )}
                      </span>
                      <span className="flex items-center gap-3">
                        <span className="font-semibold text-slate-700">{e.qty} {unit}</span>
                        <span className="text-xs text-blue-600">{e.order_count} 单</span>
                      </span>
                    </button>
                    {expanded === rowKey && (
                      <div className="flex flex-wrap gap-1.5 bg-slate-50 px-3 py-2">
                        {e.order_ids.map((oid) => (
                          <Link
                            key={oid}
                            to={`/orders/${oid}`}
                            className="rounded-md border border-slate-300 bg-white px-2 py-0.5 text-[11px] font-medium text-blue-600 hover:bg-blue-50"
                          >
                            订单 …{oid.slice(-6)}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </>
  );
}

/** 客户明细抽屉。entries 传入前应已按需要过滤（如指定票种/房型）。 */
export function UsageDrawer({
  title,
  subtitle,
  entries,
  unit,
  onClose,
  onEdit,
  loading,
}: {
  title: string;
  subtitle?: string;
  entries: UsageCustomerEntry[];
  unit: string; // 间 / 张 / 人
  onClose: () => void;
  onEdit?: () => void; // 保留编辑库存入口
  loading?: boolean;
}) {
  const sums = channelSums(entries);
  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-black/30 backdrop-blur-sm animate-in fade-in"
      onClick={onClose}
    >
      <div
        className="h-full w-full max-w-md overflow-y-auto border-l border-slate-200 bg-white p-6 shadow-xl animate-in slide-in-from-right"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-1 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
          <button onClick={onClose} className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100">
            <X size={18} />
          </button>
        </div>
        {subtitle && <p className="mb-3 text-sm text-slate-500">{subtitle}</p>}

        <ChannelStackBar sums={sums} className="mb-4 !h-2.5" />

        <UsageBreakdownList entries={entries} unit={unit} loading={loading} />

        {onEdit && (
          <div className="mt-4 border-t border-slate-200 pt-3">
            <button
              onClick={onEdit}
              className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              ✎ 编辑当日库存
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
